/**
 * Zod schemas for the benchmark customActions catalog.
 *
 * Moved from `@camstack/types/capabilities/pipeline-executor.cap.ts` (2026-04-14)
 * — benchmark shapes belong to the addon that implements them, not the
 * pipeline-executor cap. `PipelineEngineChoiceSchema` and
 * `PipelineStepInputSchema` are still imported from `@camstack/types`
 * because they describe pipeline-executor concepts the benchmark only
 * consumes (engine identity + step tree).
 */
import { z } from 'zod'
import type { PipelineStepInputOutput } from '@camstack/types'
import { HWACCEL_OPTIONS, PipelineStepInputSchema } from '@camstack/types'
import type { ReplaySource } from './replay/replay-source.js'
import type { ReplayOverlay } from './replay/replay-plan.js'
import type { OrchestrateReplayRunResult } from './replay/replay-wiring.js'
import type { StoredReplayRun } from './replay/replay-store.js'
import type { ReplayDiff } from './replay/replay-diff.js'

// Re-export the underlying type so this file fully owns the public
// surface of the schemas — keeps tsup's DTS portable.
export type { PipelineStepInputOutput }

/**
 * Pipeline benchmark history entry — output of a single `runPipelineTest`
 * (or parallel aggregate). Stored with `kind: 'pipeline'` discriminator so
 * the history list can mix decoder-perf entries without schema collisions.
 */
export const PipelineBenchmarkHistoryEntrySchema = z.object({
  kind: z.literal('pipeline').default('pipeline'),
  id: z.string(),
  date: z.string(),
  stepId: z.string(),
  modelId: z.string(),
  runtime: z.string(),
  iterations: z.number(),
  warmup: z.number(),
  fps: z.number(),
  avgMs: z.number(),
  medianMs: z.number(),
  p95Ms: z.number(),
  p99Ms: z.number(),
  /**
   * OPTIONAL, and absent on every entry the page writes.
   * `pipeline-runner.runSyntheticBench` reports mean / p50 / p95 / p99 and
   * measures no per-call min or max. They were required here while nothing
   * in the page ever called `saveBenchmarkResult`, so nothing noticed; the
   * moment it does, a required field can only be satisfied by inventing a
   * number, and a fabricated latency in the history table is worse than a
   * blank one.
   */
  minMs: z.number().optional(),
  maxMs: z.number().optional(),
  avgDetections: z.number(),
  referenceImage: z.string().optional(),
  /** Number of concurrent runs aggregated into this entry; 1 = single run. */
  parallelRuns: z.number().min(1).default(1),
})

export const PipelineBenchmarkHistoryEntryInputSchema = PipelineBenchmarkHistoryEntrySchema.omit({
  kind: true,
  id: true,
  date: true,
})

/**
 * Legacy aliases — kept to avoid breaking imports from pre-v2 consumers.
 * Both resolve to the discriminated pipeline variant.
 */
export const BenchmarkHistoryEntrySchema = PipelineBenchmarkHistoryEntrySchema
export const BenchmarkHistoryEntryInputSchema = PipelineBenchmarkHistoryEntryInputSchema

// ─── Decoder performance test ─────────────────────────────────────────────

export const DecoderFixtureCodecSchema = z.enum(['h264', 'h265'])
export const DecoderFixtureSchema = z.object({
  filename: z.string(),
  codec: DecoderFixtureCodecSchema,
  resolutionLabel: z.string(), // '360p', '1080p', '1440p'
  sizeBytes: z.number(),
  description: z.string().optional(),
})

export const CamProfileSchema = z.enum(['high', 'mid', 'low'])

export const DecoderPerfSourceSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('sample'),
    fixtureFilename: z.string(),
  }),
  z.object({
    kind: z.literal('stream'),
    deviceId: z.number().int().min(1),
    profile: CamProfileSchema,
  }),
])

export const DecoderPerfModeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('duration'), durationSec: z.number().min(5).max(1800) }),
  z.object({ kind: z.literal('iterations'), iterations: z.number().min(10).max(1_000_000) }),
])

export const DecoderPerfFormatSchema = z.enum(['jpeg', 'gray'])

// Source of truth: `HWACCEL_OPTIONS` from `@camstack/types` — the
// same list the decoder addon's `globalSettingsSchema()` advertises.
// Benchmark never maintains its own backend list; if a new backend
// lands in the addon, it becomes bench-selectable automatically.
const HWACCEL_VALUES = HWACCEL_OPTIONS.map((o) => o.value) as unknown as readonly [
  (typeof HWACCEL_OPTIONS)[number]['value'],
  ...(typeof HWACCEL_OPTIONS)[number]['value'][],
]
export const DecoderPerfHwaccelSchema = z.enum(HWACCEL_VALUES)

export const DecoderPerfThresholdsSchema = z.object({
  warnMBMin: z.number().min(0).default(5),
  failMBMin: z.number().min(0).default(20),
})

export const DecoderPerfInputSchema = z.object({
  sessionId: z.string(),
  cameras: z.number().int().min(1).max(16),
  mode: DecoderPerfModeSchema,
  source: DecoderPerfSourceSchema,
  format: DecoderPerfFormatSchema,
  fps: z.number().min(1).max(120),
  toggleMs: z.number().min(0).default(0),
  warmupSec: z.number().min(0).default(15),
  sampleIntervalMs: z.number().min(500).default(2000),
  hwaccel: DecoderPerfHwaccelSchema.default('none'),
  thresholds: DecoderPerfThresholdsSchema.default({ warnMBMin: 5, failMBMin: 20 }),
  // Pin the decoder sessions to the selected node. createSession is the only
  // decoder call without a node selector (the rest route via the nodeId that
  // createSession returns), so without this the whole test runs on the
  // singleton's elected node (the hub) regardless of the UI selection.
  nodeId: z.string().optional(),
})

export const DecoderPerfSampleSchema = z.object({
  tSec: z.number(),
  rssMB: z.number(),
  heapMB: z.number(),
  externalMB: z.number(),
  arrayBuffersMB: z.number(),
  cpuPct: z.number(),
  framesTotal: z.number(),
})

export const DecoderPerfVerdictSchema = z.enum(['pass', 'warn', 'fail', 'aborted', 'error'])

export const DecoderPerfResultSchema = z.object({
  sessionId: z.string(),
  verdict: DecoderPerfVerdictSchema,
  aborted: z.boolean().default(false),
  errorMessage: z.string().optional(),
  samples: z.array(DecoderPerfSampleSchema).readonly(),
  slopes: z.object({
    rssMBMin: z.number(),
    heapMBMin: z.number(),
    externalMBMin: z.number(),
  }),
  totals: z.object({
    wallSec: z.number(),
    framesEmitted: z.number(),
    fpsPerCamera: z.number(),
    cpuAvgPct: z.number(),
    droppedFrames: z.number(),
  }),
  config: DecoderPerfInputSchema,
})

/**
 * Progress event payload emitted on `benchmark.progress` category during a
 * decoder perf run. Discriminated via `kind: 'decoder-perf'` so the
 * existing pipeline benchmark progress listeners ignore these.
 */
export const DecoderPerfProgressSchema = z.object({
  kind: z.literal('decoder-perf'),
  sessionId: z.string(),
  phase: z.enum(['init', 'warmup', 'running', 'complete', 'aborted', 'error']),
  tSec: z.number().optional(),
  sample: DecoderPerfSampleSchema.optional(),
  message: z.string().optional(),
})

export const DecoderPerfHistoryEntrySchema = z.object({
  kind: z.literal('decoder-perf'),
  id: z.string(),
  date: z.string(),
  sessionId: z.string(),
  verdict: DecoderPerfVerdictSchema,
  cameras: z.number(),
  format: DecoderPerfFormatSchema,
  sourceLabel: z.string(), // 'sample:camera-sim-360p' | 'stream:dev-2/low'
  modeLabel: z.string(), // 'duration:60s' | 'iterations:5000'
  rssSlopeMBMin: z.number(),
  heapSlopeMBMin: z.number(),
  externalSlopeMBMin: z.number(),
  framesEmitted: z.number(),
  fpsPerCamera: z.number(),
  cpuAvgPct: z.number(),
  droppedFrames: z.number(),
  wallSec: z.number(),
  hwaccel: DecoderPerfHwaccelSchema,
})

export const DecoderPerfHistoryEntryInputSchema = DecoderPerfHistoryEntrySchema.omit({
  kind: true,
  id: true,
  date: true,
})

// ─── Unified history entry (discriminated union) ──────────────────────────

export const HistoryEntrySchema = z.discriminatedUnion('kind', [
  PipelineBenchmarkHistoryEntrySchema,
  DecoderPerfHistoryEntrySchema,
])

export interface PipelineRunInputShape {
  readonly steps: readonly PipelineStepInputOutput[]
  readonly imageBase64?: string
  readonly referenceImage?: string
  /**
   * Optional cached-frame id obtained from `cacheBenchFrame`. When set,
   * the bench addon forwards the cached raw RGB buffer to
   * `pipelineExecutor.runPipeline` via the `frame` field — bypasses
   * per-call JPEG decode + base64 transfer.
   */
  readonly frameId?: string
  readonly nodeId?: string
  readonly sessionId?: string
  /**
   * Per-run engine override. When present, the target addon builds a
   * one-shot engine for this bench call WITHOUT mutating its saved
   * `currentEngine`. Drives the "change engine in bench and see the
   * swap take effect" flow.
   */
  readonly engine?: {
    readonly runtime: 'python' | 'node'
    readonly backend: string
    readonly format: 'onnx' | 'coreml' | 'openvino' | 'tflite'
    readonly device?: string
  }
  /**
   * Inference-device selector of `nodeId`, `<backend>:<device>` —
   * `openvino:npu`, `edgetpu:usb`, `coreml:all`, `cpu`. Selects WHICH device
   * pool of the node runs the call; omitted ⇒ that node's default pool.
   *
   * The pipeline's unit of work is `(camera → node → deviceKey)` and the cap
   * has accepted this field since the Phase-2 multi-device work
   * (`pipeline-executor.cap.ts` `runPipeline.deviceKey`). This schema simply
   * dropped it, so the bench had ONE dropdown where the system has three
   * coordinates and could not name `edgetpu:usb` versus `openvino:npu` at
   * all — on a node that hosts both, "how fast is this camera's tree" had no
   * answer the bench could ask.
   */
  readonly deviceKey?: string
  /**
   * Execution plane, forwarded verbatim to `pipelineExecutor.runPipeline`.
   * `'full'` (the cap's default) runs root AND crop children in one call;
   * `'frame'` is the live per-frame dispatch — root-plane steps only, crop
   * children served per track via `runDetailSubtree`. A bench that wants to
   * measure "what a production camera frame costs" must ask for `'frame'`;
   * the default is the benchmark/reference-image shape, not the live one.
   */
  readonly plane?: 'full' | 'frame'
}

export const PipelineRunInputSchema: z.ZodType<PipelineRunInputShape> = z.object({
  steps: z.array(PipelineStepInputSchema).readonly(),
  imageBase64: z.string().optional(),
  referenceImage: z.string().optional(),
  frameId: z.string().optional(),
  /**
   * Target node for the pipeline test. Forwarded to
   * `pipelineExecutor.runPipeline`. Omit to run on the hub.
   */
  nodeId: z.string().optional(),
  /**
   * UI-provided correlation id. When set, every `pipeline.progress`
   * event emitted by the run is tagged with this value so the caller
   * can filter out concurrent camera-runtime dispatches that share the
   * same bus category.
   */
  sessionId: z.string().optional(),
  engine: z
    .object({
      runtime: z.enum(['python', 'node']),
      backend: z.string(),
      format: z.enum(['onnx', 'coreml', 'openvino', 'tflite']),
      device: z.string().optional(),
    })
    .optional(),
  deviceKey: z.string().optional(),
  plane: z.enum(['full', 'frame']).optional(),
})

/**
 * Batched pipeline test — sends N copies of the same image as one
 * `runPipelineBatch` cap call. The benchmark addon decodes the JPEG
 * once into RGB on the hub, then ships N FrameInputs in a single
 * MsgPack hop to the target node where the Python pool collapses
 * them into one `MSG_INFER_BATCH = 0x03` IPC frame. Used by the
 * Image tab when `batchSize > 1` to amortise per-call overhead.
 */
export interface PipelineRunBatchInputShape {
  readonly steps: readonly PipelineStepInputOutput[]
  readonly imageBase64: string
  readonly batchSize: number
  /**
   * Resize the decoded RGB to this square size before shipping the
   * batch over Moleculer. Stays under the cluster transporter's
   * `maxPacketSize` cap (default 10 MB) — at 640×640 a 4-frame batch
   * is ~4.9 MB. Defaults to 640.
   */
  readonly targetSize?: number
  readonly nodeId?: string
  readonly sessionId?: string
  readonly engine?: PipelineRunInputShape['engine']
}

export const PipelineRunBatchInputSchema: z.ZodType<PipelineRunBatchInputShape> = z.object({
  steps: z.array(PipelineStepInputSchema).readonly(),
  imageBase64: z.string(),
  batchSize: z.number().int().min(1).max(255),
  targetSize: z.number().int().min(64).max(2048).optional(),
  nodeId: z.string().optional(),
  sessionId: z.string().optional(),
  engine: z
    .object({
      runtime: z.enum(['python', 'node']),
      backend: z.string(),
      format: z.enum(['onnx', 'coreml', 'openvino', 'tflite']),
      device: z.string().optional(),
    })
    .optional(),
})

/**
 * Output of a batched pipeline test. Carries one `FrameResult` per
 * input frame plus aggregate timing (total cap call wall-clock and
 * the last batch's reported inference time).
 */
export const PipelineRunBatchResultSchema = z.object({
  results: z.array(PipelineRunResultBridgeRef()).readonly(),
  callMs: z.number(),
  inferMs: z.number(),
})

// Indirection because `PipelineRunResultBridge` is imported by
// `benchmark-actions.ts` from `@camstack/types` rather than re-exported
// here; keeping the resolution lazy avoids a circular import.
function PipelineRunResultBridgeRef(): z.ZodType<unknown> {
  // Schemas in this file should not pull from `@camstack/types` at
  // module init (it's a peer that may not be fully loaded). Use
  // `z.unknown()` as the wire shape — the actual `FrameResult`
  // validation happens upstream in `pipeline-executor.cap.ts`.
  return z.unknown()
}

/**
 * Multi-camera stress tester input.
 *
 * Replicates a sustained multi-camera inference workload in-process
 * so we can measure the pool's true detections-per-second ceiling
 * without paying tRPC + JPEG decode + JSON marshalling on every call.
 *
 * Workflow inside the addon:
 *   1. Decode + sharp-resize the input JPEG ONCE to `targetSize`×raw RGB.
 *   2. Pre-build the FrameInput[] templates for batched + single calls.
 *   3. Launch `simulatedCameras` parallel "cameras" that each loop
 *      `batchesPerCamera` times. Per round, dispatch:
 *      - 1× `runPipelineBatch.mutate(frames=N)` (counted as `+= batchSize`)
 *      - `extraSinglesPerRound`× `runPipelineBatch.mutate(frames=1)` (side-load)
 *   4. Aggregate detsPerSec = (simulatedCameras × batchesPerCamera × batchSize) / totalMs.
 *
 * If detection-pipeline is co-located on the same node, the cap calls
 * short-circuit through `localProviderLink` (no JSON, no TLS, no IPC).
 * This isolates the pure pool throughput so we can compare against
 * external detector-engine benchmarks.
 */
export interface MultiCameraStressInputShape {
  readonly steps: readonly PipelineStepInputOutput[]
  readonly imageBase64: string
  readonly targetSize?: number
  readonly simulatedCameras?: number
  readonly batchesPerCamera?: number
  readonly batchSize?: number
  readonly extraSinglesPerRound?: number
  readonly nodeId?: string
  readonly sessionId?: string
}

export const MultiCameraStressInputSchema: z.ZodType<MultiCameraStressInputShape> = z.object({
  steps: z.array(PipelineStepInputSchema).readonly(),
  imageBase64: z.string(),
  targetSize: z.number().int().min(64).max(2048).optional(),
  simulatedCameras: z.number().int().min(1).max(64).optional(),
  batchesPerCamera: z.number().int().min(1).max(2000).optional(),
  batchSize: z.number().int().min(1).max(64).optional(),
  extraSinglesPerRound: z.number().int().min(0).max(16).optional(),
  nodeId: z.string().optional(),
  sessionId: z.string().optional(),
})

export const MultiCameraStressResultSchema = z.object({
  totalMs: z.number(),
  countedDetections: z.number(),
  detsPerSec: z.number(),
  totalCalls: z.number(),
  totalFrames: z.number(),
  callMsP50: z.number(),
  callMsP95: z.number(),
  callMsP99: z.number(),
  inferMsMean: z.number(),
  errors: z.number(),
})

// ─── Sustained throughput (root-detection e2e) ─────────────────────────────
//
// A SUSTAINED THROUGHPUT test of ROOT detection: pin ONE frame in memory
// once (`frameId` from cacheBenchFrame, or a one-shot `imageBase64`), then
// feed the identical frame to root object-detection for `durationSec`
// seconds — driven by BATCHES (`runPipelineBatch`, `batchSize` frames per
// call) with `concurrency` calls in flight (input parallelization). Fanned
// out across `nodes`, each running its resolved runtime/model on ROOT-ONLY
// steps (the detector slot). Because the batch fast path is inherently
// single-root, root-only steps execute exactly the root-plane detection the
// live per-frame dispatch runs (`plane:'frame'`) — no cap-schema change
// needed, no crop children. Measures sustained fps + det/s + latency
// percentiles per node on the identical pinned frame.

/** One fan-out target: a node + its resolved engine + root-only step tree. */
export interface SustainedNodeTargetShape {
  /** Target node id. Omit to run on the hub. */
  readonly nodeId?: string
  /**
   * Per-node engine override (runtime/backend/format/device) resolved from
   * `getSchema({nodeId}).selectedEngine` or an operator RuntimeSelector
   * choice. Omit to run on the node's own default engine.
   */
  readonly engine?: PipelineRunInputShape['engine']
  /**
   * Inference-device selector (`<backend>:<device>`, e.g. `openvino:npu`,
   * `openvino:gpu`, `edgetpu:usb`, `cpu`). Routes this fan-out target to a
   * specific device POOL of the node — the vehicle for testing NPU / iGPU /
   * Coral CONCURRENTLY (one sustained node target per device). Omit ⇒ the
   * node's default device pool.
   */
  readonly deviceKey?: string
  /**
   * ROOT-ONLY steps — the detector slot only (no enabled crop children).
   * Resolved per node from `getSchema({nodeId})` so each node tests the
   * model it actually runs. Passing root-only steps keeps the batch call on
   * the single-root fast path == live `plane:'frame'` root detection.
   */
  readonly steps: readonly PipelineStepInputOutput[]
  /** Human label for the resolved runtime/model (report/UI only). */
  readonly label?: string
}

export interface SustainedThroughputInputShape {
  /** Pinned frame id from `cacheBenchFrame` — reused across every node/iter. */
  readonly frameId?: string
  /** One-shot fallback when no frame is pinned; decoded ONCE server-side. */
  readonly imageBase64?: string
  /** Run duration N in seconds. */
  readonly durationSec: number
  /** Frames per `runPipelineBatch` call (ML batch size). */
  readonly batchSize: number
  /** Max in-flight batch calls per node (input parallelization). */
  readonly concurrency: number
  /** Square resize applied to the pinned frame before batching. Default 640. */
  readonly targetSize?: number
  /** Correlation id — also the abort key for `abortSustainedThroughput`. */
  readonly sessionId: string
  /** Fan-out targets. */
  readonly nodes: readonly SustainedNodeTargetShape[]
}

const SustainedEngineSchema = z
  .object({
    runtime: z.enum(['python', 'node']),
    backend: z.string(),
    format: z.enum(['onnx', 'coreml', 'openvino', 'tflite']),
    device: z.string().optional(),
  })
  .optional()

const SustainedNodeTargetSchema: z.ZodType<SustainedNodeTargetShape> = z.object({
  nodeId: z.string().optional(),
  engine: SustainedEngineSchema,
  deviceKey: z.string().optional(),
  steps: z.array(PipelineStepInputSchema).readonly(),
  label: z.string().optional(),
})

export const SustainedThroughputInputSchema: z.ZodType<SustainedThroughputInputShape> = z.object({
  frameId: z.string().optional(),
  imageBase64: z.string().optional(),
  durationSec: z.number().min(1).max(3600),
  batchSize: z.number().int().min(1).max(255),
  concurrency: z.number().int().min(1).max(64),
  targetSize: z.number().int().min(64).max(2048).optional(),
  sessionId: z.string(),
  nodes: z.array(SustainedNodeTargetSchema).min(1).readonly(),
})

export const SustainedLatencyStatsSchema = z.object({
  p50: z.number(),
  p95: z.number(),
  p99: z.number(),
  mean: z.number(),
  min: z.number(),
  max: z.number(),
})

export const SustainedNodeResultSchema = z.object({
  nodeId: z.string().nullable(),
  label: z.string().optional(),
  /** Total frames processed by this node's loop. */
  totalFrames: z.number(),
  /** Total completed batch calls. */
  totalBatches: z.number(),
  /** Measured wall-clock window in ms. */
  wallMs: z.number(),
  /** Sustained throughput. */
  fps: z.number(),
  detsPerSec: z.number(),
  /** Mean detections per frame — stable since the frame is fixed. */
  avgDetections: z.number(),
  latency: SustainedLatencyStatsSchema,
  /** Batch calls that errored (transport / inference). */
  errors: z.number(),
  /** True when this node ended via the stop button rather than the deadline. */
  aborted: z.boolean(),
})

export const SustainedThroughputResultSchema = z.object({
  perNode: z.array(SustainedNodeResultSchema).readonly(),
  durationSec: z.number(),
  batchSize: z.number(),
  concurrency: z.number(),
})

// ── Replay (2026-08-26) ──────────────────────────────────────────────────
// `z.custom<T>()` bridges, not duplicated shapes — `ReplaySource` /
// `ReplayOverlay` / `OrchestrateReplayRunResult` / `StoredReplayRun` are
// plain TS types owned by `replay/*.ts`; re-declaring them in Zod here would
// be exactly the drift `CLAUDE.md` warns against ("z.infer<> for schemas,
// never duplicate a shape").
export const ReplaySourceSchema = z.custom<ReplaySource>()
export const ReplayOverlaySchema = z.custom<ReplayOverlay>()
export const OrchestrateReplayRunResultSchema = z.custom<OrchestrateReplayRunResult>()
export const StoredReplayRunSchema = z.custom<StoredReplayRun>()
export const ReplayDiffSchema = z.custom<ReplayDiff>()

export const RunReplayClipInputSchema = z.object({
  deviceId: z.number(),
  nodeId: z.string(),
  source: ReplaySourceSchema,
  overlay: ReplayOverlaySchema,
  fps: z.number().positive(),
  maxFrames: z.number().int().positive(),
  maxWidth: z.number().int().positive().default(1920),
  deviceKey: z.string().optional(),
})

export const UploadReplayClipInputSchema = z.object({
  dataBase64: z.string(),
  durationMs: z.number().positive(),
})
export const UploadReplayClipResultSchema = z.object({ clipId: z.string() })

export const ListReplayRunsInputSchema = z.object({ deviceId: z.number() })
export const DiffReplayRunsInputSchema = z.object({ runIdA: z.string(), runIdB: z.string() })
